import { of } from 'rxjs';
import type { RuntimeDataServiceInput } from '../contracts';
import { RuntimeDataService } from '../types';
import type { RuntimeDataServiceRequest, RuntimeResultLinkDescriptor } from '../types';
import { isRecord } from '../validationPrimitives';

function validateMeasuringPointContextInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point" input to be an object.');
    }

    if (typeof input.name !== 'string') {
        throw new Error('Expected "measuring_point.name" to be a string.');
    }

    if (typeof input.deviceName !== 'string' && input.deviceName !== null) {
        throw new Error('Expected "measuring_point.deviceName" to be a string or null.');
    }

    return {
        name: input.name,
        deviceName: input.deviceName,
    };
}

function validateMeasuringPointContextResult(
    result: unknown
): RuntimeDataServiceInput<'measuring_point'> {
    return validateMeasuringPointContextInput(result);
}

export class MeasuringPointContextRuntimeDataService extends RuntimeDataService<'measuring_point'> {
    static readonly serviceId = 'measuring_point' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_MEASURING_POINT_CONTEXT_LABEL'),
            description: gettext('DATA_SERVICE_MEASURING_POINT_CONTEXT_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'name',
                label: gettext('DATA_SERVICE_MEASURING_POINT_CONTEXT_NAME_LABEL'),
                description: gettext('DATA_SERVICE_MEASURING_POINT_CONTEXT_NAME_DESCRIPTION'),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: null,
                path: 'name',
                valueKind: 'scalar',
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                buildInput: ({ context }) => {
                    if (!context || context.kind !== 'measuring-point') {
                        throw new Error(
                            'Measuring-point context binding requires measuring-point render context.'
                        );
                    }

                    return {
                        name: context.measuringPoint.name,
                        deviceName: context.measuringPoint.deviceName,
                    };
                },
            },
            {
                key: 'deviceName',
                label: gettext('DEVICE_NAME'),
                description: gettext(
                    'DATA_SERVICE_MEASURING_POINT_CONTEXT_DEVICE_NAME_DESCRIPTION'
                ),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: null,
                path: 'deviceName',
                valueKind: 'scalar',
                nullPreviewFallback: gettext('REPORT_BUILDER_NO_DEVICE_ATTACHED'),
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                buildInput: ({ context }) => {
                    if (!context || context.kind !== 'measuring-point') {
                        throw new Error(
                            'Measuring-point context binding requires measuring-point render context.'
                        );
                    }

                    return {
                        name: context.measuringPoint.name,
                        deviceName: context.measuringPoint.deviceName,
                    };
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptor<'measuring_point'>[];
    }

    validateArgs = validateMeasuringPointContextInput;

    validateResult = validateMeasuringPointContextResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'measuring_point'>) => of(input);
}

export function createMeasuringPointContextService(): MeasuringPointContextRuntimeDataService {
    return new MeasuringPointContextRuntimeDataService();
}
